/**
 * PixiJS-based Terminal Renderer
 * GPU-accelerated character grid renderer for 60fps full-screen updates
 */

import { Application, Container, Graphics, Sprite, Texture, Rectangle } from 'pixi.js';
import type { CellChange } from '../shared/types.js';
import { RENDERER, SCALING, FONT_ATLAS, CURSOR, TYPEWRITER, SCROLL, PIXEL_MODE, HYBRID } from './renderer-config.js';
import { VGA_256_PALETTE } from '../shared/vga256-palette.js';

// VGA 16-color palette (as hex numbers for PixiJS tinting)
const VGA_COLORS: number[] = [
  0x000000, // 0 - Black
  0x0000AA, // 1 - Blue
  0x00AA00, // 2 - Green
  0x00AAAA, // 3 - Cyan
  0xAA0000, // 4 - Red
  0xAA00AA, // 5 - Magenta
  0xAA5500, // 6 - Brown
  0xAAAAAA, // 7 - Light Gray
  0x555555, // 8 - Dark Gray
  0x5555FF, // 9 - Light Blue
  0x55FF55, // 10 - Light Green
  0x55FFFF, // 11 - Light Cyan
  0xFF5555, // 12 - Light Red
  0xFF55FF, // 13 - Light Magenta
  0xFFFF55, // 14 - Yellow
  0xFFFFFF, // 15 - White
];

// CP437 character mapping for extended ASCII (first 256 characters)
const CP437_CHARS: string =
  ' \u263A\u263B\u2665\u2666\u2663\u2660\u2022\u25D8\u25CB\u25D9\u2642\u2640\u266A\u266B\u263C' +
  '\u25BA\u25C4\u2195\u203C\u00B6\u00A7\u25AC\u21A8\u2191\u2193\u2192\u2190\u221F\u2194\u25B2\u25BC' +
  ' !"#$%&\'()*+,-./0123456789:;<=>?' +
  '@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_' +
  '`abcdefghijklmnopqrstuvwxyz{|}~\u2302' +
  '\u00C7\u00FC\u00E9\u00E2\u00E4\u00E0\u00E5\u00E7\u00EA\u00EB\u00E8\u00EF\u00EE\u00EC\u00C4\u00C5' +
  '\u00C9\u00E6\u00C6\u00F4\u00F6\u00F2\u00FB\u00F9\u00FF\u00D6\u00DC\u00A2\u00A3\u00A5\u20A7\u0192' +
  '\u00E1\u00ED\u00F3\u00FA\u00F1\u00D1\u00AA\u00BA\u00BF\u2310\u00AC\u00BD\u00BC\u00A1\u00AB\u00BB' +
  '\u2591\u2592\u2593\u2502\u2524\u2561\u2562\u2556\u2555\u2563\u2551\u2557\u255D\u255C\u255B\u2510' +
  '\u2514\u2534\u252C\u251C\u2500\u253C\u255E\u255F\u255A\u2554\u2569\u2566\u2560\u2550\u256C\u2567' +
  '\u2568\u2564\u2565\u2559\u2558\u2552\u2553\u256B\u256A\u2518\u250C\u2588\u2584\u258C\u2590\u2580' +
  '\u03B1\u00DF\u0393\u03C0\u03A3\u03C3\u00B5\u03C4\u03A6\u0398\u03A9\u03B4\u221E\u03C6\u03B5\u2229' +
  '\u2261\u00B1\u2265\u2264\u2320\u2321\u00F7\u2248\u00B0\u2219\u00B7\u221A\u207F\u00B2\u25A0\u00A0';

/** Cursor state */
export interface Cursor {
  x: number;
  y: number;
  visible: boolean;
}

/** Cell data for rendering */
export interface Cell {
  ch: string;
  fg: number;
  bg: number;
  bl?: boolean | undefined;
  bo?: boolean | undefined;
}

/**
 * Terminal Renderer using PixiJS
 * Renders an 80x25 character grid with VGA colors
 */
export class TerminalRenderer {
  private app: Application | null = null;
  private container: Container | null = null;
  private backgroundLayer: Container | null = null;
  private foregroundLayer: Container | null = null;
  private cursorGraphics: Graphics | null = null;

  private readonly cols: number = RENDERER.COLS;
  private readonly rows: number = RENDERER.ROWS;
  private readonly cellWidth: number = RENDERER.CELL_WIDTH_PX;
  private readonly cellHeight: number = RENDERER.CELL_HEIGHT_PX;

  // Font atlas
  private fontAtlasCanvas: HTMLCanvasElement | null = null;
  private fontAtlasTexture: Texture | null = null;
  private charToIndex: Map<string, number> = new Map();
  private charTextures: Texture[] = []; // Pre-created textures for all 256 characters

  // Cell sprites
  private cellBackgrounds: Graphics[][] = [];
  private cellSprites: Sprite[][] = [];

  // Cursor state
  private cursorX: number = 0;
  private cursorY: number = 0;
  private cursorVisible: boolean = true;
  private cursorBlinkState: boolean = true;
  private cursorBlinkInterval: number | null = null;

  private isInitialized: boolean = false;
  private pendingOperations: (() => void)[] = [];
  private readonly maxPendingOperations: number = RENDERER.MAX_PENDING_OPERATIONS; // Limit queue size (Issue 10)

  // Typewriter effect queue
  private typewriterQueue: Array<{ x: number; y: number; cell: Cell }> = [];
  private typewriterInterval: number | null = null;
  private readonly typewriterDelay: number = TYPEWRITER.DELAY_MS; // ms between characters

  // Auto-scaling
  private currentScale: number = 1;
  private resizeHandler: (() => void) | null = null;

  // Cached bounding rect (invalidated on resize)
  private cachedRect: DOMRect | null = null;

  constructor(private parentContainer: HTMLElement) {}

  /**
   * Initialize the renderer
   */
  async init(): Promise<void> {
    // Calculate dimensions
    const width = this.cols * this.cellWidth;
    const height = this.rows * this.cellHeight;

    // Create font atlas first (synchronously using canvas)
    this.createFontAtlas();

    // Initialize PixiJS application
    this.app = new Application();
    await this.app.init({
      width,
      height,
      backgroundColor: 0x000000,
      antialias: false,
      resolution: 1, // Use 1 to avoid scaling issues
      autoDensity: false,
      preferWebGLVersion: 2,
    });

    // Add canvas to DOM
    this.parentContainer.appendChild(this.app.canvas);

    // Style the canvas
    const canvas = this.app.canvas as HTMLCanvasElement;
    canvas.style.display = 'block';
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    canvas.style.imageRendering = 'pixelated';
    canvas.tabIndex = 0; // Make focusable

    // Create containers
    this.container = new Container();
    this.backgroundLayer = new Container();
    this.foregroundLayer = new Container();

    this.container.addChild(this.backgroundLayer);
    this.container.addChild(this.foregroundLayer);
    this.app.stage.addChild(this.container);

    // Create font atlas texture from canvas
    this.fontAtlasTexture = Texture.from(this.fontAtlasCanvas!);

    // Pre-create all 256 character textures to avoid runtime memory allocation
    this.createCharacterTextures();

    // Initialize cell grid
    this.initCellGrid();

    // Set up cursor
    this.initCursor();

    // Start cursor blink
    this.startCursorBlink();

    // Set up auto-scaling
    this.setupAutoScale();

    this.isInitialized = true;

    // Execute pending operations
    for (const op of this.pendingOperations) {
      op();
    }
    this.pendingOperations = [];
  }

  /**
   * Set up auto-scaling to fit the window
   */
  private setupAutoScale(): void {
    // Initial scale calculation
    this.calculateAndApplyScale();

    // Listen for window resize
    this.resizeHandler = () => this.calculateAndApplyScale();
    window.addEventListener('resize', this.resizeHandler);
  }

  /**
   * Calculate the optimal scale factor and apply it
   */
  private calculateAndApplyScale(): void {
    if (!this.app) return;

    // Invalidate cached bounding rect on resize
    this.cachedRect = null;

    const canvas = this.app.canvas as HTMLCanvasElement;
    const baseWidth = this.cols * this.cellWidth;   // 720
    const baseHeight = this.rows * this.cellHeight; // 480

    // Full assembly dimensions at scale=1 (must fit entirely in window)
    // Width: monitor frame padding (30*2) + screen container (terminal + 20) = 60 + 740 = 800
    // Height: frame top (30) + screen (550) + frame bottom (15) + panel (~44) + neck (25) + base (20) = 684
    const baseAssemblyWidth = SCALING.BASE_ASSEMBLY_WIDTH;
    const baseAssemblyHeight = SCALING.BASE_ASSEMBLY_HEIGHT;

    // Leave a small margin around the assembly
    const margin = SCALING.ASSEMBLY_MARGIN;
    const availableWidth = window.innerWidth - margin * 2;
    const availableHeight = window.innerHeight - margin * 2;

    // Calculate the maximum scale that fits the full assembly
    const scaleX = availableWidth / baseAssemblyWidth;
    const scaleY = availableHeight / baseAssemblyHeight;
    const maxScale = Math.min(scaleX, scaleY);

    // Round to nearest 0.5 for near-pixel-perfect scaling
    // Integer scales are perfectly crisp, half-integer scales are still pretty good
    let scale = Math.floor(maxScale * SCALING.SCALE_PRECISION) / SCALING.SCALE_PRECISION;

    // Ensure minimum scale of 0.5 (allow smaller for very small windows)
    scale = Math.max(SCALING.MIN_SCALE_FACTOR, scale);

    // Apply the scale if it changed
    if (scale !== this.currentScale) {
      this.currentScale = scale;

      const scaledWidth = baseWidth * scale;
      const scaledHeight = baseHeight * scale;

      // Update canvas CSS size (not internal resolution)
      canvas.style.width = `${scaledWidth}px`;
      canvas.style.height = `${scaledHeight}px`;

      // Notify CSS about the new scale via custom properties
      document.documentElement.style.setProperty('--terminal-scale', scale.toString());
      document.documentElement.style.setProperty('--terminal-width', `${scaledWidth}px`);
      document.documentElement.style.setProperty('--terminal-height', `${scaledHeight}px`);
    }
  }

  /**
   * Get current scale factor
   */
  getScale(): number {
    return this.currentScale;
  }

  /**
   * Get cached canvas bounding rect (avoids layout thrash on every mousemove)
   */
  private getCanvasRect(): DOMRect | null {
    const canvas = this.app?.canvas as HTMLCanvasElement | undefined;
    if (!canvas) return null;
    if (!this.cachedRect) {
      this.cachedRect = canvas.getBoundingClientRect();
    }
    return this.cachedRect;
  }

  /**
   * Create font atlas canvas with all characters
   */
  private createFontAtlas(): void {
    const atlasWidth = FONT_ATLAS.GRID_WIDTH; // 16 chars per row
    const atlasHeight = FONT_ATLAS.GRID_HEIGHT; // 16 rows = 256 chars

    this.fontAtlasCanvas = document.createElement('canvas');
    this.fontAtlasCanvas.width = atlasWidth * this.cellWidth;
    this.fontAtlasCanvas.height = atlasHeight * this.cellHeight;

    const ctx = this.fontAtlasCanvas.getContext('2d', {
      alpha: true,
      willReadFrequently: false,
    })!;

    // Disable antialiasing for crisp pixel fonts
    ctx.imageSmoothingEnabled = false;
    (ctx as any).mozImageSmoothingEnabled = false;
    (ctx as any).webkitImageSmoothingEnabled = false;
    (ctx as any).msImageSmoothingEnabled = false;

    // Clear with transparent background
    ctx.clearRect(0, 0, this.fontAtlasCanvas.width, this.fontAtlasCanvas.height);

    // Use a crisp monospace font
    ctx.font = `${this.cellHeight - FONT_ATLAS.FONT_SIZE_OFFSET}px "Consolas", "Courier New", monospace`;
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    ctx.fillStyle = '#FFFFFF';

    // Draw all 256 CP437 characters
    for (let i = 0; i < 256; i++) {
      const char = CP437_CHARS[i] || ' ';
      const col = i % atlasWidth;
      const row = Math.floor(i / atlasWidth);
      const x = col * this.cellWidth + FONT_ATLAS.X_OFFSET;
      const y = row * this.cellHeight + FONT_ATLAS.Y_OFFSET;

      ctx.fillText(char, x, y);
      this.charToIndex.set(char, i);
    }

    // Add common printable ASCII characters that might not be in CP437
    // (they should already be there, but ensure mapping exists)
    for (let i = 32; i < 127; i++) {
      const char = String.fromCharCode(i);
      if (!this.charToIndex.has(char)) {
        this.charToIndex.set(char, i);
      }
    }
  }

  /**
   * Pre-create all 256 character textures.
   * This prevents memory leaks from creating new Texture objects during runtime.
   */
  private createCharacterTextures(): void {
    this.charTextures = [];

    for (let i = 0; i < 256; i++) {
      const col = i % FONT_ATLAS.GRID_WIDTH;
      const row = Math.floor(i / FONT_ATLAS.GRID_WIDTH);
      const frame = new Rectangle(
        col * this.cellWidth,
        row * this.cellHeight,
        this.cellWidth,
        this.cellHeight
      );

      const texture = new Texture({
        source: this.fontAtlasTexture!.source,
        frame: frame,
      });
      this.charTextures.push(texture);
    }
  }

  /**
   * Get pre-created texture for a character
   */
  private getCharTexture(char: string): Texture {
    let index = this.charToIndex.get(char);
    if (index === undefined) {
      // Default to space for unknown characters
      index = FONT_ATLAS.DEFAULT_CHAR_INDEX;
    }
    return this.charTextures[index];
  }

  /**
   * Get texture frame for a character (used during init)
   */
  private getCharFrame(char: string): Rectangle {
    let index = this.charToIndex.get(char);
    if (index === undefined) {
      // Default to space for unknown characters
      index = FONT_ATLAS.DEFAULT_CHAR_INDEX;
    }

    const col = index % FONT_ATLAS.GRID_WIDTH;
    const row = Math.floor(index / FONT_ATLAS.GRID_WIDTH);

    return new Rectangle(
      col * this.cellWidth,
      row * this.cellHeight,
      this.cellWidth,
      this.cellHeight
    );
  }

  /**
   * Initialize the cell grid
   */
  private initCellGrid(): void {
    this.cellBackgrounds = [];
    this.cellSprites = [];

    for (let y = 0; y < this.rows; y++) {
      this.cellBackgrounds[y] = [];
      this.cellSprites[y] = [];

      for (let x = 0; x < this.cols; x++) {
        // Background rectangle
        const bg = new Graphics();
        bg.rect(0, 0, this.cellWidth, this.cellHeight);
        bg.fill(0x000000);
        bg.x = x * this.cellWidth;
        bg.y = y * this.cellHeight;
        this.backgroundLayer!.addChild(bg);
        this.cellBackgrounds[y][x] = bg;

        // Foreground character sprite
        const frame = this.getCharFrame(' ');
        const texture = new Texture({
          source: this.fontAtlasTexture!.source,
          frame: frame,
        });
        const sprite = new Sprite(texture);
        sprite.x = x * this.cellWidth;
        sprite.y = y * this.cellHeight;
        sprite.tint = VGA_COLORS[7]; // Default to light gray
        this.foregroundLayer!.addChild(sprite);
        this.cellSprites[y][x] = sprite;
      }
    }
  }

  /**
   * Initialize cursor
   */
  private initCursor(): void {
    this.cursorGraphics = new Graphics();
    this.cursorGraphics.rect(0, 0, this.cellWidth, this.cellHeight);
    this.cursorGraphics.fill({ color: 0xAAAAAA, alpha: CURSOR.ALPHA });
    this.cursorGraphics.x = 0;
    this.cursorGraphics.y = 0;
    this.foregroundLayer!.addChild(this.cursorGraphics);
  }

  /**
   * Start cursor blink animation
   */
  private startCursorBlink(): void {
    if (this.cursorBlinkInterval !== null) {
      clearInterval(this.cursorBlinkInterval);
    }

    this.cursorBlinkInterval = window.setInterval(() => {
      this.cursorBlinkState = !this.cursorBlinkState;
      this.updateCursorVisibility();
    }, CURSOR.BLINK_INTERVAL_MS);
  }

  /**
   * Pause cursor blink (e.g., when power is off) - Issue 20
   */
  pauseCursorBlink(): void {
    if (this.cursorBlinkInterval !== null) {
      clearInterval(this.cursorBlinkInterval);
      this.cursorBlinkInterval = null;
    }
  }

  /**
   * Resume cursor blink (e.g., when power is on) - Issue 20
   */
  resumeCursorBlink(): void {
    if (this.cursorBlinkInterval === null) {
      this.startCursorBlink();
    }
  }

  /**
   * Update cursor visibility based on state
   */
  private updateCursorVisibility(): void {
    if (this.cursorGraphics) {
      this.cursorGraphics.visible = this.cursorVisible && this.cursorBlinkState;
    }
  }

  /**
   * Update cursor position
   */
  private updateCursorPosition(x: number, y: number, visible: boolean): void {
    this.cursorX = x;
    this.cursorY = y;
    this.cursorVisible = visible;

    if (this.cursorGraphics) {
      this.cursorGraphics.x = x * this.cellWidth;
      this.cursorGraphics.y = y * this.cellHeight;
    }
    this.updateCursorVisibility();
  }

  /**
   * Start typewriter effect processing
   */
  private startTypewriter(): void {
    if (this.typewriterInterval !== null) {
      return; // Already running
    }

    this.typewriterInterval = window.setInterval(() => {
      this.processTypewriterQueue();
    }, this.typewriterDelay);
  }

  /**
   * Process one item from the typewriter queue
   */
  private processTypewriterQueue(): void {
    if (this.typewriterQueue.length === 0) {
      // Stop the interval when queue is empty
      if (this.typewriterInterval !== null) {
        clearInterval(this.typewriterInterval);
        this.typewriterInterval = null;
      }
      return;
    }

    // Process a batch of characters for smoother rendering
    const batchSize = Math.min(TYPEWRITER.BATCH_SIZE, this.typewriterQueue.length);
    for (let i = 0; i < batchSize; i++) {
      const item = this.typewriterQueue.shift();
      if (item) {
        this.updateCellImmediate(item.x, item.y, item.cell);
      }
    }
  }

  /**
   * Queue cells for typewriter effect
   */
  private queueTypewriter(x: number, y: number, cell: Cell): void {
    this.typewriterQueue.push({ x, y, cell });
    this.startTypewriter();
  }

  /**
   * Update cell immediately (bypasses typewriter)
   */
  private updateCellImmediate(x: number, y: number, cell: Cell): void {
    if (x < 0 || x >= this.cols || y < 0 || y >= this.rows) {
      return;
    }

    // Update background
    const bg = this.cellBackgrounds[y]?.[x];
    if (bg) {
      bg.clear();
      bg.rect(0, 0, this.cellWidth, this.cellHeight);
      bg.fill(VGA_COLORS[cell.bg] ?? 0x000000);
    }

    // Update foreground character
    const sprite = this.cellSprites[y]?.[x];
    if (sprite) {
      const char = cell.ch || ' ';
      // CRITICAL FIX: Use pre-created texture instead of creating new Texture objects
      // Creating new Texture objects without destroying old ones causes massive memory leak
      sprite.texture = this.getCharTexture(char);
      sprite.tint = VGA_COLORS[cell.fg] ?? VGA_COLORS[7];
    }
  }

  /**
   * Render full screen (for fullSync) - immediate, no typewriter
   */
  renderFullScreen(cells: Cell[][], cursor: Cursor): void {
    if (!this.isInitialized) {
      // Limit pending operations queue size (Issue 10)
      if (this.pendingOperations.length < this.maxPendingOperations) {
        this.pendingOperations.push(() => this.renderFullScreen(cells, cursor));
      }
      return;
    }

    // Clear typewriter queue for full sync
    this.typewriterQueue = [];

    for (let y = 0; y < this.rows; y++) {
      for (let x = 0; x < this.cols; x++) {
        const cell = cells[y]?.[x] || { ch: ' ', fg: 7, bg: 0 };
        this.updateCellImmediate(x, y, cell);
      }
    }

    this.updateCursorPosition(cursor.x, cursor.y, cursor.visible);
  }

  /**
   * Apply delta changes (for delta updates) - with typewriter effect for new output only
   */
  applyDelta(changes: CellChange[], cursor?: Partial<Cursor>): void {
    if (!this.isInitialized) {
      // Limit pending operations queue size (Issue 10)
      if (this.pendingOperations.length < this.maxPendingOperations) {
        this.pendingOperations.push(() => this.applyDelta(changes, cursor));
      }
      return;
    }

    // Determine the cursor row (input line)
    const cursorRow = cursor?.y ?? this.cursorY;

    // Count how many unique rows are being updated
    const rowsAffected = new Set(changes.map(c => c.r));

    // Heuristic to detect scrolling vs typing (Issue 30):
    // - Scrolling affects many rows at once (entire screen shifts)
    // - Typing affects only 1-2 rows (current line + maybe previous)
    // Threshold of 3 chosen because:
    // - Single line output: 1 row
    // - Line wrap: 2 rows
    // - Multi-line command output: typically affects 3+ rows
    const isLikelyScroll = rowsAffected.size > SCROLL.DETECTION_THRESHOLD_ROWS;

    for (const change of changes) {
      const cell: Cell = {
        ch: change.ch,
        fg: change.fg,
        bg: change.bg,
      };
      if (change.bl !== undefined) cell.bl = change.bl;
      if (change.bo !== undefined) cell.bo = change.bo;

      // Determine if this should use typewriter effect:
      // - Not a scroll operation
      // - Not the cursor row (input line)
      // - Is the row just above the cursor (most recent output)
      const isInputRow = change.r === cursorRow;
      const isRecentOutput = !isLikelyScroll && change.r === cursorRow - 1;

      if (isRecentOutput) {
        this.queueTypewriter(change.c, change.r, cell);
      } else {
        this.updateCellImmediate(change.c, change.r, cell);
      }
    }

    if (cursor) {
      if (cursor.x !== undefined && cursor.y !== undefined) {
        this.cursorX = cursor.x;
        this.cursorY = cursor.y;
        if (this.cursorGraphics) {
          this.cursorGraphics.x = cursor.x * this.cellWidth;
          this.cursorGraphics.y = cursor.y * this.cellHeight;
        }
      }
      if (cursor.visible !== undefined) {
        this.cursorVisible = cursor.visible;
        this.updateCursorVisibility();
      }
    }
  }

  /**
   * Get cursor position for testing
   */
  getCursorPosition(): { x: number; y: number; visible: boolean; pixelX: number; pixelY: number } {
    const rect = this.getCanvasRect();
    if (!rect) {
      return { x: 0, y: 0, visible: false, pixelX: 0, pixelY: 0 };
    }

    const scaleX = rect.width / (this.cols * this.cellWidth);
    const scaleY = rect.height / (this.rows * this.cellHeight);

    return {
      x: this.cursorX,
      y: this.cursorY,
      visible: this.cursorVisible && this.cursorBlinkState,
      pixelX: rect.left + (this.cursorX * this.cellWidth * scaleX),
      pixelY: rect.top + (this.cursorY * this.cellHeight * scaleY),
    };
  }

  /**
   * Get canvas element
   */
  getCanvas(): HTMLCanvasElement {
    return this.app?.canvas as HTMLCanvasElement;
  }

  /**
   * Get dimensions
   */
  getDimensions(): { cols: number; rows: number; width: number; height: number } {
    return {
      cols: this.cols,
      rows: this.rows,
      width: this.cols * this.cellWidth,
      height: this.rows * this.cellHeight,
    };
  }

  /**
   * Convert screen pixel coordinates to terminal character coordinates
   * Returns null if coordinates are outside the terminal area
   */
  screenToTerminal(screenX: number, screenY: number): { x: number; y: number } | null {
    const rect = this.getCanvasRect();
    if (!rect) {
      return null;
    }

    // Check if point is inside canvas
    if (screenX < rect.left || screenX > rect.right ||
        screenY < rect.top || screenY > rect.bottom) {
      return null;
    }

    // Calculate scale factors
    const scaleX = rect.width / (this.cols * this.cellWidth);
    const scaleY = rect.height / (this.rows * this.cellHeight);

    // Convert to terminal coordinates
    const termX = Math.floor((screenX - rect.left) / (this.cellWidth * scaleX));
    const termY = Math.floor((screenY - rect.top) / (this.cellHeight * scaleY));

    // Clamp to valid range
    const x = Math.max(0, Math.min(this.cols - 1, termX));
    const y = Math.max(0, Math.min(this.rows - 1, termY));

    return { x, y };
  }

  /**
   * Focus the renderer (for keyboard input)
   */
  focus(): void {
    const canvas = this.app?.canvas as HTMLCanvasElement | undefined;
    if (canvas) {
      canvas.focus();
    }
  }

  /**
   * Destroy the renderer
   */
  destroy(): void {
    // Remove resize listener
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }

    if (this.cursorBlinkInterval !== null) {
      clearInterval(this.cursorBlinkInterval);
      this.cursorBlinkInterval = null;
    }

    if (this.typewriterInterval !== null) {
      clearInterval(this.typewriterInterval);
      this.typewriterInterval = null;
    }

    // Destroy pre-created character textures
    for (const texture of this.charTextures) {
      texture.destroy();
    }
    this.charTextures = [];

    if (this.fontAtlasTexture) {
      this.fontAtlasTexture.destroy(true);
      this.fontAtlasTexture = null;
    }

    if (this.app) {
      this.app.destroy(true, { children: true, texture: true });
      this.app = null;
    }

    this.fontAtlasCanvas = null;
    this.container = null;
    this.backgroundLayer = null;
    this.foregroundLayer = null;
    this.cursorGraphics = null;
    this.cellBackgrounds = [];
    this.cellSprites = [];
    this.typewriterQueue = [];
  }
}

// =============================================================================
// Pixel Mode Renderer (320x200)
// =============================================================================

/** Pixel region for delta updates */
export interface PixelRegion {
  x: number;
  y: number;
  w: number;
  h: number;
  data: string | Uint8Array;  // Base64 string or raw bytes
}

/**
 * Pixel Renderer for 320x200 true pixel graphics mode
 * Uses HTML5 Canvas for direct pixel manipulation
 */
export class PixelRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private imageData: ImageData;
  private pixelData: Uint8ClampedArray;

  private readonly width = PIXEL_MODE.WIDTH;
  private readonly height = PIXEL_MODE.HEIGHT;
  private visible = false;

  constructor(private parentContainer: HTMLElement) {
    // Create canvas
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    this.canvas.style.display = 'none';
    this.canvas.style.position = 'absolute';
    this.canvas.style.top = '0';
    this.canvas.style.left = '0';
    this.canvas.style.imageRendering = 'pixelated';
    this.canvas.style.pointerEvents = 'none'; // Allow clicks to pass through

    // Get context
    this.ctx = this.canvas.getContext('2d', { alpha: false })!;
    this.ctx.imageSmoothingEnabled = false;

    // Create image data buffer
    this.imageData = this.ctx.createImageData(this.width, this.height);
    this.pixelData = this.imageData.data;

    // Initialize to black
    this.clear(0);

    // Add to container
    parentContainer.appendChild(this.canvas);
  }

  /**
   * Show the pixel canvas
   */
  show(): void {
    this.visible = true;
    this.canvas.style.display = 'block';
  }

  /**
   * Hide the pixel canvas
   */
  hide(): void {
    this.visible = false;
    this.canvas.style.display = 'none';
  }

  /**
   * Check if visible
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Clear to a single color
   */
  clear(colorIndex: number): void {
    const [r, g, b] = VGA_RGB[colorIndex] || [0, 0, 0];
    for (let i = 0; i < this.pixelData.length; i += PIXEL_MODE.RGBA_BYTES_PER_PIXEL) {
      this.pixelData[i] = r;
      this.pixelData[i + 1] = g;
      this.pixelData[i + 2] = b;
      this.pixelData[i + 3] = PIXEL_MODE.ALPHA_MAX;
    }
    this.flush();
  }

  /**
   * Apply a delta update (changed regions)
   */
  applyDelta(regions: PixelRegion[]): void {
    for (const region of regions) {
      // Support both base64 string and raw Uint8Array for performance
      const data = typeof region.data === 'string'
        ? base64ToBytes(region.data)
        : region.data;
      this.applyRegion(region.x, region.y, region.w, region.h, data);
    }
    this.flush();
  }

  /**
   * Apply a full frame sync
   */
  applyFullSync(_width: number, _height: number, data: string): void {
    const bytes = base64ToBytes(data);
    this.applyFullFrame(bytes);
    this.flush();
  }

  /**
   * Apply a region of pixel data (8-bit, 256 colors)
   */
  private applyRegion(rx: number, ry: number, rw: number, rh: number, data: Uint8Array): void {
    let dataIndex = 0;
    for (let py = 0; py < rh; py++) {
      for (let px = 0; px < rw; px++) {
        const colorIndex = data[dataIndex++];
        this.setPixelRGBA(rx + px, ry + py, colorIndex);
      }
    }
  }

  /**
   * Apply full frame data (8-bit, 256 colors)
   */
  private applyFullFrame(data: Uint8Array): void {
    let dataIndex = 0;
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        const colorIndex = data[dataIndex++];
        this.setPixelRGBA(x, y, colorIndex);
      }
    }
  }

  /**
   * Set a pixel to a VGA color
   */
  private setPixelRGBA(x: number, y: number, colorIndex: number): void {
    if (x < 0 || x >= this.width || y < 0 || y >= this.height) return;

    const [r, g, b] = VGA_RGB[colorIndex] || [0, 0, 0];
    const offset = (y * this.width + x) * 4;
    this.pixelData[offset] = r;
    this.pixelData[offset + 1] = g;
    this.pixelData[offset + 2] = b;
    this.pixelData[offset + 3] = PIXEL_MODE.ALPHA_MAX;
  }

  /**
   * Flush the image data to the canvas
   */
  private flush(): void {
    this.ctx.putImageData(this.imageData, 0, 0);
  }

  /**
   * Update canvas size to match terminal renderer scale
   */
  updateScale(terminalCanvas: HTMLCanvasElement): void {
    const rect = terminalCanvas.getBoundingClientRect();
    this.canvas.style.width = `${rect.width}px`;
    this.canvas.style.height = `${rect.height}px`;
  }

  /**
   * Get the canvas element
   */
  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  /**
   * Destroy the renderer
   */
  destroy(): void {
    if (this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
  }
}

// VGA 256-color RGB values (for pixel mode - supports DOS games)
const VGA_RGB: [number, number, number][] = VGA_256_PALETTE;

/**
 * Decode base64 to Uint8Array (browser-compatible)
 */
function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// =============================================================================
// Hybrid Mode Renderer (text UI with embedded pixel regions)
// =============================================================================

/** Hybrid pixel region definition */
export interface HybridPixelRegionDef {
  id: string;
  cellX: number;
  cellY: number;
  cellWidth: number;
  cellHeight: number;
}

/** Cell dimensions in pixels */
const CELL_WIDTH_PX = RENDERER.CELL_WIDTH_PX;
const CELL_HEIGHT_PX = RENDERER.CELL_HEIGHT_PX;

/**
 * Single Hybrid Region Canvas
 * Manages a pixel canvas for one region of the terminal
 */
class HybridRegionCanvas {
  readonly id: string;
  readonly cellX: number;
  readonly cellY: number;
  readonly cellWidth: number;
  readonly cellHeight: number;
  readonly pixelWidth: number;
  readonly pixelHeight: number;

  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private imageData: ImageData;
  private pixelData: Uint8ClampedArray;

  constructor(def: HybridPixelRegionDef, parentContainer: HTMLElement) {
    this.id = def.id;
    this.cellX = def.cellX;
    this.cellY = def.cellY;
    this.cellWidth = def.cellWidth;
    this.cellHeight = def.cellHeight;
    this.pixelWidth = def.cellWidth * CELL_WIDTH_PX;
    this.pixelHeight = def.cellHeight * CELL_HEIGHT_PX;

    // Create canvas
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.pixelWidth;
    this.canvas.height = this.pixelHeight;
    this.canvas.style.position = 'absolute';
    this.canvas.style.imageRendering = 'pixelated';
    this.canvas.style.pointerEvents = 'none';
    this.canvas.style.display = 'block';
    this.canvas.dataset.regionId = this.id;

    // Get context
    this.ctx = this.canvas.getContext('2d', { alpha: false })!;
    this.ctx.imageSmoothingEnabled = false;

    // Create image data buffer
    this.imageData = this.ctx.createImageData(this.pixelWidth, this.pixelHeight);
    this.pixelData = this.imageData.data;

    // Initialize to black
    this.clear(0);

    // Add to container
    parentContainer.appendChild(this.canvas);
  }

  /**
   * Update canvas position based on terminal scale
   */
  updatePosition(terminalCanvas: HTMLCanvasElement): void {
    const rect = terminalCanvas.getBoundingClientRect();
    const scaleX = rect.width / (HYBRID.TERMINAL_COLS * CELL_WIDTH_PX);
    const scaleY = rect.height / (HYBRID.TERMINAL_ROWS * CELL_HEIGHT_PX);

    // Position the canvas
    const pixelX = this.cellX * CELL_WIDTH_PX;
    const pixelY = this.cellY * CELL_HEIGHT_PX;

    this.canvas.style.left = `${pixelX * scaleX}px`;
    this.canvas.style.top = `${pixelY * scaleY}px`;
    this.canvas.style.width = `${this.pixelWidth * scaleX}px`;
    this.canvas.style.height = `${this.pixelHeight * scaleY}px`;
  }

  /**
   * Clear to a single color
   */
  clear(colorIndex: number): void {
    const [r, g, b] = VGA_RGB[colorIndex] || [0, 0, 0];
    for (let i = 0; i < this.pixelData.length; i += PIXEL_MODE.RGBA_BYTES_PER_PIXEL) {
      this.pixelData[i] = r;
      this.pixelData[i + 1] = g;
      this.pixelData[i + 2] = b;
      this.pixelData[i + 3] = PIXEL_MODE.ALPHA_MAX;
    }
    this.flush();
  }

  /**
   * Apply a delta update (changed regions)
   */
  applyDelta(regions: PixelRegion[]): void {
    for (const region of regions) {
      // Support both base64 string and raw Uint8Array for performance
      const data = typeof region.data === 'string'
        ? base64ToBytes(region.data)
        : region.data;
      this.applyRegion(region.x, region.y, region.w, region.h, data);
    }
    this.flush();
  }

  /**
   * Apply a full frame sync
   */
  applyFullSync(width: number, height: number, data: string): void {
    // Verify dimensions match
    if (width !== this.pixelWidth || height !== this.pixelHeight) {
      console.warn(`Hybrid region size mismatch: expected ${this.pixelWidth}x${this.pixelHeight}, got ${width}x${height}`);
    }

    const bytes = base64ToBytes(data);
    this.applyFullFrame(bytes);
    this.flush();
  }

  /**
   * Apply a region of pixel data (8-bit, 256 colors)
   */
  private applyRegion(rx: number, ry: number, rw: number, rh: number, data: Uint8Array): void {
    let dataIndex = 0;
    for (let py = 0; py < rh; py++) {
      for (let px = 0; px < rw; px++) {
        if (dataIndex >= data.length) return;
        const colorIndex = data[dataIndex++];
        this.setPixelRGBA(rx + px, ry + py, colorIndex);
      }
    }
  }

  /**
   * Apply full frame data (8-bit, 256 colors)
   */
  private applyFullFrame(data: Uint8Array): void {
    let dataIndex = 0;
    for (let y = 0; y < this.pixelHeight; y++) {
      for (let x = 0; x < this.pixelWidth; x++) {
        if (dataIndex >= data.length) return;
        const colorIndex = data[dataIndex++];
        this.setPixelRGBA(x, y, colorIndex);
      }
    }
  }

  /**
   * Set a pixel to a VGA color (256 colors)
   */
  private setPixelRGBA(x: number, y: number, colorIndex: number): void {
    if (x < 0 || x >= this.pixelWidth || y < 0 || y >= this.pixelHeight) return;

    const [r, g, b] = VGA_RGB[colorIndex] || [0, 0, 0];
    const offset = (y * this.pixelWidth + x) * 4;
    this.pixelData[offset] = r;
    this.pixelData[offset + 1] = g;
    this.pixelData[offset + 2] = b;
    this.pixelData[offset + 3] = PIXEL_MODE.ALPHA_MAX;
  }

  /**
   * Flush the image data to the canvas
   */
  private flush(): void {
    this.ctx.putImageData(this.imageData, 0, 0);
  }

  /**
   * Check if a cell coordinate is inside this region
   */
  containsCell(cellX: number, cellY: number): boolean {
    return cellX >= this.cellX && cellX < this.cellX + this.cellWidth &&
           cellY >= this.cellY && cellY < this.cellY + this.cellHeight;
  }

  /**
   * Destroy the canvas
   */
  destroy(): void {
    if (this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
  }
}

/**
 * Hybrid Mode Renderer
 * Manages multiple pixel regions within the terminal
 */
export class HybridModeRenderer {
  private parentContainer: HTMLElement;
  private terminalCanvas: HTMLCanvasElement | null = null;
  private regions: Map<string, HybridRegionCanvas> = new Map();
  private enabled: boolean = false;

  constructor(parentContainer: HTMLElement) {
    this.parentContainer = parentContainer;
  }

  /**
   * Set hybrid mode state
   */
  setHybridMode(enabled: boolean, regionDefs: HybridPixelRegionDef[]): void {
    this.enabled = enabled;

    if (enabled) {
      // Create new region IDs set
      const newIds = new Set(regionDefs.map(def => def.id));

      // Remove regions that are no longer defined
      for (const [id, region] of this.regions) {
        if (!newIds.has(id)) {
          region.destroy();
          this.regions.delete(id);
        }
      }

      // Add new regions or update existing
      for (const def of regionDefs) {
        if (!this.regions.has(def.id)) {
          const region = new HybridRegionCanvas(def, this.parentContainer);
          if (this.terminalCanvas) {
            region.updatePosition(this.terminalCanvas);
          }
          this.regions.set(def.id, region);
        }
      }
    } else {
      // Clear all regions
      for (const region of this.regions.values()) {
        region.destroy();
      }
      this.regions.clear();
    }
  }

  /**
   * Update a specific region with pixel data
   */
  updateRegion(regionId: string, regions: PixelRegion[]): void {
    const region = this.regions.get(regionId);
    if (region) {
      region.applyDelta(regions);
    }
  }

  /**
   * Full sync a specific region
   */
  fullSyncRegion(regionId: string, width: number, height: number, data: string): void {
    const region = this.regions.get(regionId);
    if (region) {
      region.applyFullSync(width, height, data);
    }
  }

  /**
   * Check if a cell is inside any pixel region
   */
  isInPixelRegion(cellX: number, cellY: number): boolean {
    if (!this.enabled) return false;
    for (const region of this.regions.values()) {
      if (region.containsCell(cellX, cellY)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Update positions of all region canvases based on terminal scale
   */
  updateScale(terminalCanvas: HTMLCanvasElement): void {
    this.terminalCanvas = terminalCanvas;
    for (const region of this.regions.values()) {
      region.updatePosition(terminalCanvas);
    }
  }

  /**
   * Check if hybrid mode is enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Destroy all regions
   */
  destroy(): void {
    for (const region of this.regions.values()) {
      region.destroy();
    }
    this.regions.clear();
    this.enabled = false;
  }
}
